import { useCallback, useEffect, useState } from "react";
import { API_URLS, getQuoteStatusUrl } from "../config/environment";
import { useAuth } from "../context/context";
import { useTokenValidation } from "./useTokenValidation";

/**
 * Hook unificado para manejar cotizaciones tanto para usuarios como para backoffice
 * @param {Object} options - Opciones de configuración
 * @param {boolean} options.isBackoffice - Si true, muestra todas las cotizaciones y permite actualizar status
 * @param {number} options.itemsPerPage - Número de items por página (default: 10 para user, 25 para backoffice)
 * @param {Object} options.defaultFilters - Filtros por defecto
 */
export const useQuotesManager = (options = {}) => {
  const {
    isBackoffice = false,
    itemsPerPage = isBackoffice ? 25 : 10,
    defaultFilters = {},
  } = options;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage,
  });
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    date_from: null,
    date_to: null,
    search: "",
    page: 1,
    limit: itemsPerPage,
    ...defaultFilters,
  });
  const [updateLoading, setUpdateLoading] = useState({});

  const { user, token } = useAuth();
  const { ensureValidToken, handleApiResponse } = useTokenValidation();

  const fetchQuotes = useCallback(
    async (newFilters = {}) => {
      if (!user || !token) return;

      setLoading(true);
      try {
        // Validar token antes de hacer la petición
        await ensureValidToken();

        const currentFilters = { ...filters, ...newFilters };
        const params = new URLSearchParams();

        // Agregar filtros válidos
        Object.entries(currentFilters).forEach(([key, value]) => {
          if (value && value !== "" && value !== null) {
            if (key === "date_from" || key === "date_to") {
              if (value instanceof Date) {
                params.append(key, value.toISOString().split("T")[0]);
              }
            } else {
              params.append(key, value);
            }
          }
        });

        // Para usuarios normales, solo mostrar sus cotizaciones
        if (!isBackoffice) {
          params.append("own_only", "true");
        }

        const logPrefix = isBackoffice ? "[Backoffice]" : "[User]";
        console.log(
          `🔍 ${logPrefix} Fetching quotes with filters:`,
          currentFilters
        );

        const response = await fetch(`${API_URLS.QUOTES}?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        // Manejar respuesta con validación de token
        const isValidResponse = await handleApiResponse(response);
        if (!isValidResponse) {
          return; // Token expirado, handleApiResponse ya maneja el logout
        }

        const data = await response.json();

        if (response.ok && data.success) {
          setQuotes(data.data || []);
          setPagination(
            data.pagination || {
              currentPage: 1,
              totalPages: 1,
              totalItems: 0,
              itemsPerPage,
            }
          );
          console.log(`✅ ${logPrefix} Quotes loaded:`, data.data?.length || 0);
        } else {
          console.error(`❌ ${logPrefix} Error fetching quotes:`, data);
          throw new Error(
            data.error?.message || "Error al cargar cotizaciones"
          );
        }
      } catch (error) {
        if (error.message.includes("Token expirado")) {
          const logPrefix = isBackoffice ? "[Backoffice]" : "[User]";
          console.warn(`🔐 ${logPrefix} Token expirado al cargar cotizaciones`);
          return;
        }
        console.error(
          `❌ ${isBackoffice ? "[Backoffice]" : "[User]"} Network error:`,
          error
        );
        setQuotes([]);
      } finally {
        setLoading(false);
      }
    },
    [
      user,
      token,
      filters,
      ensureValidToken,
      handleApiResponse,
      isBackoffice,
      itemsPerPage,
    ]
  );

  /**
   * Actualizar status y/o prioridad de una cotización (solo para backoffice)
   */
  const updateQuoteStatusPriority = useCallback(
    async (quoteId, updates) => {
      if (!isBackoffice) {
        throw new Error(
          "La actualización de cotizaciones solo está disponible en backoffice"
        );
      }

      if (!updates.status && !updates.priority) {
        throw new Error("Debe proporcionar al menos status o priority");
      }

      if (!token) {
        throw new Error("Token requerido para actualizar cotizaciones");
      }

      setUpdateLoading((prev) => ({ ...prev, [quoteId]: true }));

      try {
        // Validar token antes de la operación crítica
        await ensureValidToken();

        console.log(`🔄 [Backoffice] Updating quote ${quoteId}:`, updates);

        const response = await fetch(getQuoteStatusUrl(quoteId), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        });

        // Manejar respuesta con validación de token
        const isValidResponse = await handleApiResponse(response);
        if (!isValidResponse) {
          throw new Error("Token expirado");
        }

        const data = await response.json();

        if (response.ok && data.success) {
          console.log("✅ [Backoffice] Quote updated successfully:", data.data);

          // Actualizar la cotización en el estado local
          setQuotes((prevQuotes) =>
            prevQuotes.map((quote) =>
              quote.id === quoteId
                ? { ...quote, ...updates, updated_at: new Date().toISOString() }
                : quote
            )
          );

          return data.data;
        } else {
          console.error("❌ [Backoffice] Error updating quote:", data);
          throw new Error(
            data.error?.message || "Error al actualizar cotización"
          );
        }
      } catch (error) {
        if (error.message.includes("Token expirado")) {
          console.warn(
            "🔐 [Backoffice] Token expirado al actualizar cotización"
          );
          throw error;
        }
        console.error("❌ [Backoffice] Network error updating quote:", error);
        throw error;
      } finally {
        setUpdateLoading((prev) => ({ ...prev, [quoteId]: false }));
      }
    },
    [isBackoffice, token, ensureValidToken, handleApiResponse]
  );

  useEffect(() => {
    fetchQuotes();
  }, [user, filters.page]); // Solo depender de user y page para evitar loops

  const updateFilters = useCallback(
    (newFilters) => {
      const updatedFilters = { ...filters, ...newFilters, page: 1 };
      setFilters(updatedFilters);
      fetchQuotes(updatedFilters);
    },
    [filters, fetchQuotes]
  );

  const changePage = useCallback((page) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const refresh = useCallback(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  // Estadísticas calculadas
  const stats = useCallback(() => {
    if (!quotes.length)
      return {
        total: 0,
        pending: 0,
        inProgress: 0,
        completed: 0,
        rejected: 0,
        cancelled: 0,
      };

    return {
      total: pagination.totalItems,
      pending: quotes.filter((q) => q.status === "pending").length,
      inProgress: quotes.filter((q) => q.status === "in_progress").length,
      completed: quotes.filter((q) => q.status === "completed").length,
      rejected: quotes.filter((q) => q.status === "rejected").length,
      cancelled: quotes.filter((q) => q.status === "cancelled").length,
    };
  }, [quotes, pagination.totalItems]);

  return {
    // Estado
    quotes,
    loading,
    pagination,
    filters,
    updateLoading,

    // Acciones
    updateFilters,
    changePage,
    refresh,
    fetchQuotes,

    // Solo para backoffice
    ...(isBackoffice && { updateQuoteStatusPriority }),

    // Utilidades
    stats: stats(),
    isBackoffice,
  };
};
